<template>
  <div
    id="modal-overlay"
    v-show="isExpand && (betslips.length || results.length)"
    @click="handleModalClick"
  ></div>
  <div
    class="betslip-container"
    :class="{
      expanded: isExpand && (betslips.length || results.length),
      'is-api-loading': showBetslipLoadingOverlay,
    }"
  >
    <div
      v-if="showBetslipLoadingOverlay"
      class="betslip-loading-overlay"
      aria-busy="true"
      aria-live="polite"
    >
      <Loading :isComponent="true" :isLoadingPage="false" />
    </div>
    <OverlayPopup
      :show="showExpiredOverlay"
      title="Event Expired"
      message="Some bets have expired and will be removed."
      @close="closeExpiredOverlay"
    />
    <div
      id="betslip-header"
      class="betslip-header"
      v-if="results.length == 0"
      @click="handleCollap()"
    >
      <p class="betslip__title">
        {{ getTranslatedText("View Bets") }} <span>{{ betslips.length }}</span>
      </p>

      <div class="betslip__right">
        <p class="notify" v-if="isShowNotify">
          {{ getTranslatedText("successfully added to betslip") }}
        </p>
        <p class="icon" v-if="betslips.length > 0 && !isShowNotify">
          <i v-if="isExpand" class="bi bi-x"></i>
        </p>
      </div>
    </div>
    <div
      class="betslip__table--wrapper"
      :class="{
        collapsed: isExpand,
      }"
    >
      <template v-if="results.length == 0 && betslips.length > 0">
        <div class="betslip-table" id="betslip-table">
          <div
            v-for="bet in betslips"
            :key="bet.id"
            :id="`bet__${bet.id}`"
            v-memo="[
              bet.id,
              bet.wager,
              bet.returnValue,
              bet.next_draws,
              bet.isSavedNumbers,
              bet.myNumberId,
              bet.nextDrawsShowInfo?.length,
              expandedFutureBet === bet.id,
              futureDraws.get(bet.id),
              removingBetId === bet.id,
            ]"
          >
            <div class="betslip-table__row">
              <div class="row bet__name">
                <p class="infor-bet__type">
                  {{ getTranslatedText(bet.gameName) }} -
                  {{ getTranslatedText(bet.outComeName) }}
                </p>
                <p class="infor-bet__date">
                    {{ renderDateTimeText(bet.drawDate) }}
                  </p>
              </div>

              <div class="bet__numbers desktop">
                <div v-if="bet.numbers.length > 0" class="numbers">
                  <Number
                    :customClass="'number__in-list'"
                    v-for="(n, i) in bet.numbers"
                    :key="i"
                    :number="n.value"
                    :bgColor="n.color"
                  />
                </div>
                <p v-else>{{ bet.marketOptionName }}</p>
              </div>

              <div class="row bet__actions">
                <div v-if="bet.numbers.length > 0" class="numbers">
                  <Number
                    :customClass="'number__in-list'"
                    v-for="(n, i) in bet.numbers"
                    :key="i"
                    :number="n.value"
                    :bgColor="n.color"
                  />
                </div>
                <p v-else>{{ bet.marketOptionName }}</p>

                <div class="action">
                  <Tooltip :content="getTranslatedText('save to my numbers')">
                    <div
                      class="save-numbers-button"
                      @click="handleSaveMyNumber(bet)"
                    >
                      <i :class="savedNumberIconClass(bet.isSavedNumbers)"></i>
                    </div>
                  </Tooltip>

                  <div
                    class="trash-button"
                    :class="{ 'is-removing': removingBetId === bet.id }"
                    @click="removeBetslipItem(bet)"
                  >
                    <i :class="removeButtonIconClass(bet.id)"></i>
                  </div>
                </div>
              </div>

              <div class="row bet__odd">
                <span>{{ getTranslatedText("odds") }}</span>
                <span class="odds__value"> {{ formatPrice(bet.odds) }}</span>
              </div>

              <div class="row bet__amount">
                <InputAmount
                  :key="bet.id"
                  :className="'custom'"
                  @amount-value="handleReceiveAmount"
                  :itemId="bet.id"
                  :amountDefault="bet.wager"
                  :settings="bet.betSettings || {}"
                  :odds="bet.odds"
                />
              </div>

              <div class="row bet__pwining">
                <p>{{ getTranslatedText("Potential Winings") }}</p>
                <p class="winnings__value">
                  {{ currencySymbol }} {{ formatPrice(bet.returnValue) }}
                </p>
              </div>

              <div class="row bet__countdown">
                <Countdown
                  :drawDate="bet.drawDate"
                  :betId="bet.id"
                  @bet-expired="removeExpiredBet"
                />
              </div>

              <div class="row betslip-table__draws">
                <div
                  class="futurebet__container"
                  :class="{ open: expandedFutureBet == bet.id }"
                >
                  <button
                    class="dropdown-button"
                    @click="onExpandFutureBet(bet.id)"
                  >
                    <span>{{ formatFutureDrawLabel(futureDraws.get(bet.id) || 0) }}</span>

                    <i :class="futureBetChevronClass(bet.id)"></i>
                  </button>
                  <ul class="dropdown-options">
                    <li
                      v-for="draws in [0, 5, 10, 20]"
                      :key="draws"
                      @click="handleShowNextData(draws, bet)"
                    >
                      <div class="dropdown__item">
                        <i
                          class="bi bi-check-circle-fill"
                          :class="{ 'is-selected': isFutureDrawSelected(bet.id, draws) }"
                        ></i>
                        {{ formatFutureDrawLabel(draws) }}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div
                v-if="bet.nextDrawsShowInfo?.length"
                class="row betslip-table__draws betslip-table__draws-infor"
              >
                <p>
                  {{ getTranslatedText("next") }} {{ bet.next_draws }}
                  <span v-for="(item, i) in bet.nextDrawsShowInfo" :key="i">
                    <span class="index">{{ i + 1 }}</span
                    >: {{ renderDateTimeText(item.draw_date) }}&nbsp;
                  </span>
                </p>
              </div>

              <div class="row bet__actions desktop action">
                <Tooltip :content="getTranslatedText('save to my numbers')">
                  <div
                    class="save-numbers-button"
                    @click="handleSaveMyNumber(bet)"
                  >
                    <i :class="savedNumberIconClass(bet.isSavedNumbers)"></i>
                  </div>
                </Tooltip>

                <div
                  class="trash-button"
                  :class="{ 'is-removing': removingBetId === bet.id }"
                  @click="removeBetslipItem(bet)"
                >
                  <i :class="removeButtonIconClass(bet.id)"></i>
                </div>
              </div>
            </div>

            <div
              v-if="bet.nextDrawsShowInfo?.length"
              class="row betslip-table__draws betslip-table__draws-infor desktop"
            >
              <p>
                {{ getTranslatedText("next") }} {{ bet.next_draws }}
                <span v-for="(item, i) in bet.nextDrawsShowInfo" :key="i">
                  <span class="index">{{ i + 1 }}</span
                  >: {{ renderDateTimeText(item.draw_date) }}&nbsp;
                </span>
              </p>
            </div>
          </div>
        </div>
      </template>
      <ResultComponent v-if="results.length > 0" :results="results" />
      <div
        class="betslip-footer"
        :class="{ 'bet-placed': results.length > 0 }"
        v-if="results.length > 0 || betslips.length > 0"
      >
        <template v-if="betslips.length > 0">
          <ButtonCommon v-if="!isSubmit"
            text="remove all"
            class="button__secondary"
            @action="removeAll"
          />
          <p v-if="!isSubmit" class="betslip-footer__title" @click="placeBet">
            {{
              $store.state.isLogin
                ? `${getTranslatedText("place bet")} ${currencySymbol}
                        ${formatPrice(sumWagger)}`
                : getTranslatedText("login")
            }}
          </p>
          <Loading
            v-else
            :isComponent="true"
            :isLoadingPage="false"
          />
          <p class="betslip-footer__return">
            {{ getTranslatedText("Total potential winnings") }}
            <span>{{ currencySymbol }} {{ formatPrice(totalReturn) }}</span>
          </p>
        </template>

        <template v-if="results.length > 0">
          <p class="betslip-footer__status">
            <span class="bet-placed-text"
              ><i class="bi bi-check-circle-fill"></i
            ></span>
            <span class="bet-placed-text">{{
              getTranslatedText("bet placed")
            }}</span>
          </p>
          <div class="betslip-footer__infor">
            <p>
              {{ getTranslatedText("stake") }}
              <span>{{ currencySymbol }} {{ formatPrice(totaStake) }}</span>
            </p>
            <p>
              {{ getTranslatedText("Total Potential Winings") }}
              <span>{{ currencySymbol }} {{ formatPrice(totalReturn) }}</span>
            </p>
            <ButtonCommon
              text="close"
              class="button__primary"
              @action="handleClose"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import languageMixins from "@/mixins/language-mixins";
import utilsMixin from "@/mixins/function-utils";
import ButtonCommon from "@/components/common/button";
import Number from "@/components/common/number";
import InputAmount from "@/components/common/input/input-amount.vue";
import ResultComponent from "./components/result.vue";
import Loading from "@/components/common/loading/loading.vue";
import EventBus from "@/utils/EventBus";
import Tooltip from "@/components/common/tooltip/index.vue";
import OverlayPopup from "@/components/common/OverlayPopup.vue";
import betslipService from "@/services/betslips/index.service.ts";
import HomeService from "@/services/home/index.service.ts";
import { getStateStore } from "@/utils/utils.ts";
import {
  applyBetWager,
  calculateBetReturnValue,
  isBetPayoutCapped,
  resolveNextDrawMultiplier,
} from "@/utils/betslip-transform";
import {
  persistBetslipDraftToStorage,
  syncBetslipDraftToStore,
} from "@/services/betslip-persistence";
import { notifyApiError } from "@/utils/notify";
import myNumberService from "@/services/my-numbers/index.service.ts";
import Countdown from "./countdown.vue";

const BETSLIP_LOADING_OVERLAY_DELAY_MS = 200;
const BETSLIP_REMOVE_MIN_SPINNER_MS = 280;

export default {
  mixins: [languageMixins, utilsMixin],
  components: {
    Tooltip,
    ButtonCommon,
    Number,
    InputAmount,
    ResultComponent,
    Loading,
    Countdown,
    OverlayPopup,
  },
  data() {
    return {
      isExpand: false,
      isShowNotify: false,
      isSubmit: false,
      betslips: [],
      results: [],
      nextDraws: [],
      totaStake: 0,
      currencySymbol: "",
      persistBetslipTimer: null,
      showBetslipLoadingOverlay: false,
      betslipLoadingOverlayTimer: null,
      showExpiredOverlay: false,
      expiredBets: [],
      expandedFutureBet: "",
      futureDraws: new Map(),
      removingBetId: null,
    };
  },
  computed: {
    totalReturn() {
      if (this.results.length > 0) {
        return this.results.reduce(
          (sum, result) =>
            sum +
            calculateBetReturnValue(
              result.wager,
              result.odds,
              result.nextdrawsNumber
            ),
          0
        );
      }
      return this.betslips.reduce((sum, bet) => sum + bet.returnValue, 0);
    },
    sumWagger() {
      return this.betslips.reduce(
        (sum, bet) => sum + bet.wager * this.getBetDrawCount(bet),
        0
      );
    },
    isBetslipApiLoading() {
      return this.$store.state.stateBetslip.isBetslipApiLoading;
    },
  },
  watch: {
    isBetslipApiLoading: {
      handler(active) {
        this.updateBetslipLoadingOverlay(Boolean(active));
      },
      immediate: true,
    },
    "$store.state.accountKey"() {
      this.handleGetDataFromStore();
    },
    "$store.state.stateBetslip.showAddedNotify"(value) {
      if (!value) return;
      this.handleGetDataFromStore(true);
      this.results = [];
      this.isShowNotify = true;
      this.$store.commit("setShowAddedNotify", false);
      this.$store.commit("setActiveAmountItemId", null);
      setTimeout(() => {
        this.isShowNotify = false;
      }, 1000);
    },
  },
  created() {
    this.handleGetDataFromStore();
    this.unregisterBeforeRouteUpdate = this.$router.afterEach((to) => {
      if (to.params.gameSlug) {
        this.handleGetNextDraw(to.params.gameSlug);
      }
    });
    EventBus.on("remove-result", this.handleRemoveItemResult);
    this.handleGetNextDraw(this.$route.params.gameSlug);
    this.unsubscribeStoreHydrate = this.$store.subscribe((mutation) => {
      if (mutation.type !== "saveBetslips") {
        return;
      }
      if (mutation.payload.key !== this.$store.state.accountKey) {
        return;
      }
      if (this.betslips.length > 0) {
        return;
      }
      if ((mutation.payload.value?.length ?? 0) > 0) {
        this.handleGetDataFromStore();
      }
    });
  },
  unmounted() {
    this.clearBetslipLoadingOverlayTimer();
    this.flushPersistBetslips();
    if (this.unsubscribeStoreHydrate) {
      this.unsubscribeStoreHydrate();
    }
    if (this.unregisterBeforeRouteUpdate) {
      this.unregisterBeforeRouteUpdate();
    }
    EventBus.off("remove-result", this.handleRemoveItemResult);
  },
  methods: {
    clearBetslipLoadingOverlayTimer() {
      if (this.betslipLoadingOverlayTimer) {
        clearTimeout(this.betslipLoadingOverlayTimer);
        this.betslipLoadingOverlayTimer = null;
      }
    },

    updateBetslipLoadingOverlay(active) {
      this.clearBetslipLoadingOverlayTimer();
      if (!active) {
        this.showBetslipLoadingOverlay = false;
        return;
      }
      this.betslipLoadingOverlayTimer = setTimeout(() => {
        this.betslipLoadingOverlayTimer = null;
        if (this.isBetslipApiLoading) {
          this.showBetslipLoadingOverlay = true;
        }
      }, BETSLIP_LOADING_OVERLAY_DELAY_MS);
    },

    mergeStoreBetslipsWithLocal(fromStore) {
      if (this.betslips.length === 0) {
        return fromStore;
      }
      const localById = new Map(this.betslips.map((bet) => [bet.id, bet]));
      return fromStore.map((storeBet) => {
        const local = localById.get(storeBet.id);
        if (!local) {
          return storeBet;
        }
        return {
          ...storeBet,
          wager: local.wager,
          returnValue: local.returnValue,
          next_draws: local.next_draws ?? storeBet.next_draws,
          nextDrawsShowInfo:
            local.nextDrawsShowInfo ?? storeBet.nextDrawsShowInfo,
        };
      });
    },

    handleGetDataFromStore(mergeLocalEdits = false) {
      const { betslips } = getStateStore().stateBetslip;
      const { currency_symbol } = getStateStore().accountSetting;
      const accountKey = this.$store.state.accountKey;

      const fromStore = betslips[accountKey] ?? [];
      this.betslips = mergeLocalEdits
        ? this.mergeStoreBetslipsWithLocal(fromStore)
        : fromStore;
      this.currencySymbol = currency_symbol;

      if (mergeLocalEdits) {
        syncBetslipDraftToStore(accountKey, this.betslips);
      }

      this.betslips.forEach((bet) => {
        if (bet.next_draws != null) {
          this.futureDraws.set(bet.id, bet.next_draws);
        }
      });

      if (this.betslips.length > 0) {
        this.handleCalculateBottom();
      }
    },

    commitBetslipsToStore() {
      this.$store.commit("saveBetslips", {
        key: this.$store.state.accountKey,
        value: this.betslips,
      });
    },

    schedulePersistBetslips() {
      if (this.persistBetslipTimer) {
        clearTimeout(this.persistBetslipTimer);
      }
      const accountKey = this.$store.state.accountKey;
      const betslips = this.betslips;
      this.persistBetslipTimer = setTimeout(() => {
        this.persistBetslipTimer = null;
        persistBetslipDraftToStorage(accountKey, betslips);
      }, 300);
    },

    flushPersistBetslips() {
      if (this.persistBetslipTimer) {
        clearTimeout(this.persistBetslipTimer);
        this.persistBetslipTimer = null;
      }
      const accountKey = this.$store.state.accountKey;
      syncBetslipDraftToStore(accountKey, this.betslips);
      persistBetslipDraftToStorage(accountKey, this.betslips);
    },

    removeAll() {
      if (this.persistBetslipTimer) {
        clearTimeout(this.persistBetslipTimer);
        this.persistBetslipTimer = null;
      }
      const accountKey = this.$store.state.accountKey;
      this.betslips = [];
      this.isExpand = false;
      this.handleCalculateBottom();
      persistBetslipDraftToStorage(accountKey, []);
      syncBetslipDraftToStore(accountKey, []);
    },

    async removeBetslipItem(bet) {
      if (this.removingBetId != null) {
        return;
      }
      const betId = bet.id;
      const accountKey = this.$store.state.accountKey;
      const next = this.betslips.filter((x) => x.id != bet.id);
      if (next.length === this.betslips.length) {
        return;
      }

      this.removingBetId = betId;
      await this.$nextTick();

      const startedAt = Date.now();
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      persistBetslipDraftToStorage(accountKey, next);
      syncBetslipDraftToStore(accountKey, next);

      const remaining =
        BETSLIP_REMOVE_MIN_SPINNER_MS - (Date.now() - startedAt);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }

      this.betslips = next;
      this.handleCalculateBottom();
      if (this.removingBetId === betId) {
        this.removingBetId = null;
      }
    },

    handleCollap() {
      if (this.betslips.length == 0 && this.results.length == 0) return;

      this.isExpand = !this.isExpand;
      this.handleCalculateBottom();
    },

    isBetPayoutCapped,
    applyBetWager,

    getBetDrawCount(bet) {
      const draws = bet.next_draws ?? this.futureDraws.get(bet.id);
      return resolveNextDrawMultiplier(draws);
    },

    formatFutureDrawLabel(draws) {
      if (!draws) return this.getTranslatedText("Current Draw only");
      return `Next ${draws} ${draws > 1 ? "draws" : "draw"}`;
    },

    isFutureDrawSelected(betId, draws) {
      return (this.futureDraws.get(betId) || 0) === draws;
    },

    savedNumberIconClass(isSaved) {
      return isSaved ? "bi bi-star-fill" : "bi bi-star";
    },

    removeButtonIconClass(betId) {
      return this.removingBetId === betId
        ? "bi bi-arrow-repeat trash-button__spinner"
        : "bi bi-x";
    },

    futureBetChevronClass(betId) {
      return this.expandedFutureBet === betId
        ? "bi bi-chevron-up"
        : "bi bi-chevron-down";
    },

    handleReceiveAmount(data) {
      if (this.isBetslipApiLoading || this.isSubmit) {
        return;
      }
      const bet = this.betslips.find((x) => x.id == data.itemId);
      if (!bet) {
        return;
      }
      this.applyBetWager(bet, data.amount);
      const accountKey = this.$store.state.accountKey;
      syncBetslipDraftToStore(accountKey, this.betslips);
      this.schedulePersistBetslips();
    },

    async placeBet() {
      if (this.isSubmit || this.isBetslipApiLoading) {
        return;
      }
      this.flushPersistBetslips();
      parent.postMessage({ command: "scrollTop" }, "*");
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      if (!this.$store.state.isLogin) {
        parent.postMessage({ command: "login" }, "*");
        return;
      }

      try {
        this.isSubmit = true;
        let res = await betslipService.placeBets({
          bets: this.handlePrepareBet(),
        });
        if (res.status == "success") {
          this.results = this.handleConvertResult(res.result);
          this.betslips = [];
          this.handleCalculateBottom();
          // calculate position
          this.handleCalculateBottom();
          // save to store
          this.$store.commit("saveBetslips", {
            key: this.$store.state.accountKey,
            value: this.betslips,
          });
          await this.$store.dispatch("stateBalance/fetchBalance");
          // refreshbalance
          parent.postMessage({ command: "refreshbalance" }, "*");
        }
        this.isSubmit = false;
      } catch (error) {
        notifyApiError(error);
        this.isSubmit = false;
      }
    },
    handlePrepareBet() {
      return this.betslips.map((item) => {
        return {
          wager: item.wager,
          event_id: item.eventId,
          market_options_id: item.outComeId,
          combination: false,
          numbers:
            item.numbers.length > 0
              ? [item.numbers.map((n) => n.value)]
              : [[], []],
          next_draws: item.next_draws ? item.next_draws : null,
        };
      });
    },

    handleConvertResult(results) {
      this.totaStake = 0;
      const gameNumbers = this.$store.state.gameMarket;
      return results.map((r) => {
        let numbers = gameNumbers.find((x) => x.slug == r.game_slug)?.balls;
        if (r.next_draws != null) {
          this.totaStake += r.bets[0].wager * r.next_draws;
        } else {
          this.totaStake += r.bets[0].wager;
        }
        return {
          betId: r.bet_id,
          gameName: r.game_name,
          reference: r.reference,
          pick: r.bets[0].market_options_id,
          odds: r.bets[0].odds,
          marketName: r.bets[0].market_name,
          marketOptionName: r.bets[0].market_options_name,
          drawDate: r.bets[0].draw_date,
          wager: r.bets[0].wager,
          nextdrawsNumber: r.next_draws,
          numbers: r.bets[0].numbers,
          numbersConvert: this.filterAndSortNumbers(
            numbers,
            r.bets[0].numbers[0]
          ),
          nextDrawsData: r.next_draws_data,
        };
      });
    },

    handleRemoveItemResult(item) {
      const index = this.results.findIndex((x) => x.betId == item.betId);
      if (index > -1) {
        this.results.splice(index, 1);
      }
      this.handleCalculateBottom();
    },

    handleClose() {
      this.isExpand = false;
      this.betslips = [];
      this.results = [];
      this.handleCalculateBottom();
    },

    async handleGetNextDraw(gameSlug) {
      if (!gameSlug) {
        this.nextDraws = [];
        return;
      }

      try {
        let res = await HomeService.getNextDraws();
        if (res.status === "success") {
          this.nextDraws =
            res.result.find((x) => x.slug == gameSlug)?.draws ?? [];

          this.$store.commit("SET_NEXT_DRAWS", res.result);
        }
      } catch (error) {
        notifyApiError(error);
      }
    },

    getDrawsForGame(gameSlug) {
      const fromStore = this.$store.state.nextDraws.find(
        (x) => x.slug === gameSlug
      )?.draws;

      if (fromStore?.length) {
        return fromStore;
      }

      if (gameSlug && this.$route.params.gameSlug === gameSlug) {
        return this.nextDraws ?? [];
      }

      return [];
    },
    onExpandFutureBet(betId) {
      if (this.$store.state.isScreenMobile) {
        const betTableContainer = document.getElementById("betslip-table");
        const tableRow = document
          .getElementById(`bet__${betId}`)
          .getElementsByClassName("betslip-table__row")[0];

        setTimeout(() => {
          betTableContainer.scrollTo({
            // scroll to top of selector
            top:
              document.getElementById(`bet__${betId}`).offsetTop +
              tableRow.clientHeight -
              // minus height of dropdown
              150,
            behavior: "smooth",
          });
        }, 100);
      }

      this.expandedFutureBet = betId;
      this.handleCalculateBottom();
    },

    async handleShowNextData(numberTake, item) {
      if (this.$store.state.isScreenMobile) {
        const betTableContainer = document.getElementById("betslip-table");

        setTimeout(() => {
          betTableContainer.scrollTo({
            top: document.getElementById(`bet__${item.id}`).offsetTop - 58,
            behavior: "smooth",
          });
        }, 100);
      }

      this.expandedFutureBet = "";
      this.futureDraws.set(item.id, numberTake);

      const index = this.betslips.findIndex((x) => x.id == item.id);
      if (index > -1) {
        const bet = this.betslips[index];
        bet.next_draws = numberTake;
        this.applyBetWager(bet, bet.wager);

        let drawsForBet = this.getDrawsForGame(item.gameSlug);
        if (numberTake > 0 && drawsForBet.length === 0) {
          await this.handleGetNextDraw(item.gameSlug);
          drawsForBet = this.getDrawsForGame(item.gameSlug);
        }

        bet.nextDrawsShowInfo =
          numberTake > 0 ? drawsForBet.slice(0, numberTake) : [];
      }
      this.handleCalculateBottom();
      this.commitBetslipsToStore();
    },

    async handleSaveMyNumber(r) {
      try {
        const index = this.betslips.indexOf(r);
        if (index == -1) return;
        if (r.isSavedNumbers) {
          let res = await myNumberService.removemyNumber(r.myNumberId);

          if (res.status == "success") {
            this.betslips[index].isSavedNumbers = false;
            delete this.betslips[index].myNumberId;
          }
        } else {
          const payload = {
            market_options_id: +r.outComeId,
            numbers: [r.numbers.map((item) => +item.value)],
          };
          let res = await myNumberService.saveMyNumber(payload);
          if (res.status == "success") {
            this.betslips[index].isSavedNumbers = true;
            this.betslips[index].myNumberId = res.result.id;
          }
        }
        this.commitBetslipsToStore();
        EventBus.emit("reload-my-numbers");
      } catch (error) {
        notifyApiError(error);
      }
    },
    removeExpiredBet(betId) {
      const accountKey = this.$store.state.accountKey;
      const next = this.betslips.filter((bet) => bet.id !== betId);
      if (next.length === this.betslips.length) {
        return;
      }
      this.betslips = next;
      this.expiredBets.push(betId);
      this.showExpiredOverlay = true;
      persistBetslipDraftToStorage(accountKey, next);
      syncBetslipDraftToStore(accountKey, next);
      this.handleCalculateBottom();
    },
    closeExpiredOverlay() {
      this.showExpiredOverlay = false;
      this.expiredBets = [];
    },
    handleModalClick() {
      if (this.betslips.length <= 0) {
        this.isExpand = false;
        this.handleCalculateBottom();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/public/scss/components/betslip/index.scss";

#modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;
}
</style>
